import './styles.scss';
import React, {Dispatch, useCallback, useState} from "react";

/* types, constants, utils */
import Utils from "../../../utils";
import * as Constants from "../../../constants";

/* components */
import ViewerSearchList from "./viewer-record-lists/viewer-search-list";
import ViewerMoreLikeList from "./viewer-record-lists/viewer-more-like-list";
import ViewerBatesBrowseList from "./viewer-record-lists/viewer-bates-browse-list";
import ViewerDuplicatesList from "./viewer-record-lists/viewer-duplicates-list";
import {ListGroup} from "react-bootstrap";
import {setRecordTool} from "../../../redux/slices/viewer-slice";
import {AnyAction} from "@reduxjs/toolkit";
import {useDispatch, useSelector} from "react-redux";

import {RootState} from "../../../redux";
import {IViewerState} from "../../../types/redux/viewer";
import {useSearchParams} from "react-router-dom";

const ViewerRecords = ():JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  /* redux */
  const viewerState: IViewerState = useSelector<RootState, IViewerState>(state => state.viewer);
  
  /* state */
  const [numOfRecords, setNumOfRecords] = useState<number>(null);

  /* local */
  const hasQuery: boolean = (searchParams.get(Constants.QUERY_KEY) !== null);

  /* hooks */
  const dispatch:Dispatch<AnyAction> = useDispatch();

  const handleToolSelect = useCallback((recordTool: string | null): void => {
    searchParams.set(Constants.RECORD_TOOL_PARAM, recordTool);
    setSearchParams(searchParams)
    dispatch(setRecordTool(recordTool));
  },[dispatch, searchParams, setSearchParams])

  const results = useCallback(():JSX.Element => {
    let jsx: JSX.Element = <></>;
    switch (viewerState.recordTool) {
      case Constants.SEARCH_RESULTS:
        if (hasQuery){
          jsx = <ViewerSearchList
            setNumOfRecords={setNumOfRecords}
          />
        }
        break;

      case Constants.BROWSE:
        jsx = <ViewerBatesBrowseList
          setNumOfRecords={setNumOfRecords}
        />
        break;

      case Constants.POSSIBLE_DUPLICATES:
        if (viewerState.initialRecord && viewerState.initialRecord.signature) {
          jsx = <ViewerDuplicatesList
            setNumOfRecords={setNumOfRecords}
          />
        }
        break;

      case Constants.BATES:
        /* if bates not available do not display tab */
        if (viewerState.initialRecord && viewerState.initialRecord.bn) {
          jsx = <ViewerBatesBrowseList
            setNumOfRecords={setNumOfRecords}
          />
        }
        break;

      case Constants.MORE_LIKE_THIS:
        jsx = <ViewerMoreLikeList
          setNumOfRecords={setNumOfRecords}
        />
        break;

      default:
        /* no case */
    }

    return jsx;
  }, [setNumOfRecords, viewerState.initialRecord, viewerState.recordTool, hasQuery]);

  const recordCardMenu = useCallback(():JSX.Element =>{
    return (
      <ListGroup as="ul" className={'d-block flex-unset px-3 pt-4 pb-2 border-bottom border-top'}>
        {/* Search */}
        <ListGroup.Item
          action as="li"
          className={(viewerState.recordTool === Constants.SEARCH_RESULTS) ? ' active': ''}
          onClick={()=>handleToolSelect(Constants.SEARCH_RESULTS)}
        >
          <div><strong>{Utils.toTitleCase(Constants.RECORDS)}</strong></div>
        </ListGroup.Item>

        {/* Browse */}
        <ListGroup.Item
          action as="li"
          className={(viewerState.recordTool === Constants.BROWSE) ? ' active': ''}
          onClick={()=>handleToolSelect(Constants.BROWSE)}
        >
          <div><strong>{Utils.toTitleCase(Constants.BROWSE)}</strong></div>
        </ListGroup.Item>

        {/* Duplicates */}
        {(viewerState.initialRecord && viewerState.initialRecord.signature) &&
          <ListGroup.Item
            action as="li"
            className={(viewerState.recordTool === Constants.POSSIBLE_DUPLICATES) ? ' active': ''}
            onClick={()=>handleToolSelect(Constants.POSSIBLE_DUPLICATES)}
          >
            <div><strong>{Utils.toTitleCase(Constants.POSSIBLE_DUPLICATES)}</strong></div>
          </ListGroup.Item>}

        {/* Bates */}
        {(viewerState.initialRecord.bn) &&
          <ListGroup.Item
            action as="li"
            className={(viewerState.recordTool === Constants.BATES) ? ' active': ''}
            onClick={()=>handleToolSelect(Constants.BATES)}
          >
          <div><strong>{Utils.toTitleCase(Constants.BATES)}</strong></div>
        </ListGroup.Item>}

        {/* More like this */}
        <ListGroup.Item
          action as="li"
          className={(viewerState.recordTool === Constants.MORE_LIKE_THIS) ? ' active': ''}
          onClick={()=>handleToolSelect(Constants.MORE_LIKE_THIS)}
        >
          <div><strong>{Utils.toTitleCase(Constants.MORE_LIKE_THIS)}</strong></div>
        </ListGroup.Item>
      </ListGroup>
    )
  }, [viewerState.recordTool, viewerState.initialRecord, handleToolSelect]);

  return (
    <div className={'viewer-records position-relative mt-4 ' + viewerState.recordTool}>
      <div className={'d-lg-none bg-white'}>
        {viewerState.initialRecord && recordCardMenu()}
      </div>

      {/* Num of Results */}
      {(numOfRecords !== null) &&
        <div className={'num-records border-bottom pb-2'}>
          <p className={"ps-3 mb-0 mt-2"}>
            {Utils.formatNumber(numOfRecords)}
            <span>&nbsp;results</span>
          </p>
        </div>}

      { /* Results*/ }
      { results() }
    </div>
  )
}
export default ViewerRecords;
